import React, { useState } from "react";
import { Mail, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { colors, newsletter } from "../config/site";

/**
 * Newsletter signup block. Lives in the Footer.
 *
 * Behavior:
 *   - If `newsletter.enabled === false` (current state): renders a "Coming Soon"
 *     placeholder card with the eyebrow + note. The input is visible but disabled,
 *     so visitors see the intent and the spot is "reserved" in the layout.
 *   - When `newsletter.enabled === true` and `newsletter.action` is set: POSTs to
 *     the Mailchimp form action. Mailchimp returns its own confirmation page in
 *     a new tab.
 */
const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const isLive = !!newsletter.enabled && !!newsletter.action;

  return (
    <div
      className="rounded-2xl p-6 lg:p-7"
      style={{
        backgroundColor: "rgba(255,255,255,0.05)",
        border: `1px dashed ${colors.gold}aa`,
      }}
      data-testid="newsletter-signup"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4" style={{ color: colors.gold }} />
        <p
          className="text-xs uppercase tracking-widest font-bold"
          style={{ color: colors.gold }}
        >
          {isLive ? "Stay in the Loop" : "Newsletter · Coming Soon"}
        </p>
      </div>
      <h4 className="text-lg font-semibold text-white mb-2">
        {isLive
          ? "Monthly program updates & resources"
          : "Newsletter signup is on the way"}
      </h4>
      <p className="text-sm text-stone-300 leading-relaxed mb-4">
        {isLive
          ? "Get new program announcements, fresh resources, and housing-stability tips in your inbox once a month. No spam — unsubscribe anytime."
          : newsletter.comingSoonNote}
      </p>

      <form
        action={isLive ? newsletter.action : undefined}
        method="post"
        target={isLive ? "_blank" : undefined}
        onSubmit={(e) => {
          if (!isLive) e.preventDefault();
        }}
        className="flex flex-col sm:flex-row gap-2"
        data-testid="newsletter-form"
      >
        <Input
          type="email"
          name="EMAIL"
          required={isLive}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!isLive}
          placeholder={isLive ? "your@email.com" : "Reserved — launching soon"}
          className="flex-1 bg-white/95 text-charcoal placeholder:text-stone-500 disabled:opacity-70"
          data-testid="newsletter-email-input"
        />
        <Button
          type="submit"
          disabled={!isLive}
          className="rounded-md font-semibold disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            backgroundColor: isLive ? colors.terracotta : colors.gold,
            color: isLive ? colors.white : colors.charcoal,
          }}
          data-testid="newsletter-submit"
        >
          {isLive ? (
            <>
              Subscribe
              <ArrowRight className="ml-1.5 w-4 h-4" />
            </>
          ) : (
            <>
              <Mail className="mr-1.5 w-4 h-4" />
              Notify Me
            </>
          )}
        </Button>
      </form>
      {!isLive && (
        <p className="text-[11px] text-stone-400 mt-3 italic">
          We&apos;ll turn this on once our Mailchimp account is set up.
        </p>
      )}
    </div>
  );
};

export default NewsletterSignup;
